import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  organMapTemplatesService,
  CreateOrganMapTemplateInput,
  UpdateOrganMapTemplateInput,
} from "../../services/admin/organMapTemplates.service";

export function useOrganMapTemplatesAdmin() {
  return useQuery({
    queryKey: ["admin", "organ_map_templates"],
    queryFn: organMapTemplatesService.getAll,
  });
}

export function useOrganMapTemplate(id?: string) {
  return useQuery({
    queryKey: ["admin", "organ_map_templates", id],
    queryFn: () => organMapTemplatesService.getById(id!),
    enabled: !!id,
  });
}

export function useCreateOrganMapTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateOrganMapTemplateInput) =>
      organMapTemplatesService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "organ_map_templates"],
      });
      queryClient.invalidateQueries({ queryKey: ["organ_map_templates"] });
      notifications.show({
        title: "Success",
        message: "Organ map template created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create organ map template",
        color: "red",
      });
    },
  });
}

export function useUpdateOrganMapTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateOrganMapTemplateInput) =>
      organMapTemplatesService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "organ_map_templates"],
      });
      queryClient.invalidateQueries({ queryKey: ["organ_map_templates"] });
      notifications.show({
        title: "Success",
        message: "Organ map template updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update organ map template",
        color: "red",
      });
    },
  });
}

export function useToggleOrganMapTemplateActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      organMapTemplatesService.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "organ_map_templates"],
      });
      queryClient.invalidateQueries({ queryKey: ["organ_map_templates"] });
      notifications.show({
        title: "Success",
        message: "Organ map template status updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update organ map template status",
        color: "red",
      });
    },
  });
}

export function useDeleteOrganMapTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => organMapTemplatesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "organ_map_templates"],
      });
      queryClient.invalidateQueries({ queryKey: ["organ_map_templates"] });
      notifications.show({
        title: "Success",
        message: "Organ map template deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete organ map template",
        color: "red",
      });
    },
  });
}

export function useOrganMapTemplateLinkedSources(templateId?: string) {
  return useQuery({
    queryKey: ["admin", "organ_map_templates", templateId, "linked-sources"],
    queryFn: () => organMapTemplatesService.getLinkedSources(templateId!),
    enabled: !!templateId,
  });
}

export function useLinkSourcesToOrganMapTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      templateId,
      sourceIds,
    }: {
      templateId: string;
      sourceIds: string[];
    }) => organMapTemplatesService.linkSources(templateId, sourceIds),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          "admin",
          "organ_map_templates",
          variables.templateId,
          "linked-sources",
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin", "organ_map_templates"],
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "sources"] });
      notifications.show({
        title: "Success",
        message: "Sources linked successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to link sources",
        color: "red",
      });
    },
  });
}

export function useUpdateSubsitePosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      templateId,
      subsiteId,
      positionX,
      positionY,
    }: {
      templateId: string;
      subsiteId: string;
      positionX: number | null;
      positionY: number | null;
    }) =>
      organMapTemplatesService.updateSubsitePosition({
        templateId,
        subsiteId,
        positionX,
        positionY,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          "admin",
          "organ_map_templates",
          variables.templateId,
          "linked-sources",
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin", "organ_map_templates"],
      });
      queryClient.invalidateQueries({ queryKey: ["report-preview"] });
      queryClient.invalidateQueries({
        queryKey: ["case-for-pathologist-review"],
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update position",
        color: "red",
      });
    },
  });
}
